import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import BoatCard from "@/components/BoatCard";
import RecommendedBoats from "@/components/RecommendedBoats";
import { Search } from "lucide-react";

const BOAT_TYPES = ["sailboat", "motor", "catamaran", "other"];
const emptyFilters = {
  q: "", type: "", min_length: "", max_length: "",
  country: "", location: "", min_share_price: "", max_share_price: "",
  sort: "recent",
};

export default function Explore() {
  const { t } = useLang();
  const { user } = useAuth();
  const [filters, setFilters] = useState(emptyFilters);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p[k] = v;
    });
    return p;
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      api.get("/explore/boats", { params })
        .then((r) => setBoats(r.data))
        .catch(() => setBoats([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [params]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const clear = () => setFilters(emptyFilters);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
      {/* Header */}
      <div className="max-w-3xl">
        <p className="eyebrow" data-testid="explore-eyebrow">Bevela</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-[#0B192C] leading-[1.05]" data-testid="explore-title">
          {t.explore.hero}
        </h1>
        <p className="mt-5 text-slate-600 leading-relaxed" data-testid="explore-subtitle">
          {t.explore.heroSubtitle}
        </p>
      </div>

      {user && <RecommendedBoats />}

      {/* Search bar */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-[1fr_240px] gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
          <input
            className="input-elegant pl-11"
            placeholder={t.explore.searchPlaceholder}
            value={filters.q}
            onChange={set("q")}
            data-testid="explore-search"
          />
        </div>
        <select className="input-elegant" value={filters.sort} onChange={set("sort")} data-testid="explore-sort">
          <option value="recent">{t.explore.sortRecent}</option>
          <option value="price_asc">{t.explore.sortPriceAsc}</option>
          <option value="price_desc">{t.explore.sortPriceDesc}</option>
          <option value="length_desc">{t.explore.sortLengthDesc}</option>
        </select>
      </div>

      {/* Type chips */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="explore-type-chips">
        <button
          onClick={() => setFilters((f) => ({ ...f, type: "" }))}
          className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filters.type === "" ? "bg-[#0B192C] text-white border-[#0B192C]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          data-testid="explore-type-all"
        >
          {t.explore.allTypes}
        </button>
        {BOAT_TYPES.map((k) => (
          <button
            key={k}
            onClick={() => setFilters((f) => ({ ...f, type: k }))}
            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filters.type === k ? "bg-[#0B192C] text-white border-[#0B192C]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
            data-testid={`explore-type-${k}`}
          >
            {t.boats.types[k]}
          </button>
        ))}
      </div>

      {/* Result count */}
      <p className="mt-10 text-xs font-display uppercase tracking-widest text-slate-400" data-testid="explore-count">
        {loading ? "…" : `${boats.length} ${t.explore.results}`}
      </p>

      {/* Grid */}
      {loading ? (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : boats.length === 0 ? (
        <div className="mt-6 border border-dashed border-slate-200 rounded-2xl p-16 text-center" data-testid="explore-empty">
          <p className="text-slate-500">{t.explore.noResults}</p>
          <button className="btn-outline text-sm mt-6" onClick={clear} data-testid="explore-empty-clear">
            {t.explore.clearFilters}
          </button>
        </div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="explore-grid">
          {boats.map((b) => (
            <BoatCard key={b.id} boat={b} testIdPrefix="explore-card" />
          ))}
        </div>
      )}
    </div>
  );
}
